/**
 * Models for the twelve piece variants.
 *
 * The shapes come from `public/models/pieces.glb`, generated by
 * `blender/generate_pieces.py`. This module keeps everything else: the
 * armature, the materials and the per-piece assembly. Each Blender part is
 * authored around the same pivot and at the same dimensions as the primitive
 * it replaced, so the animator's poses did not have to move.
 *
 * Every figure is built on the same armature so the animator can pose any of
 * them with one set of joint names:
 *
 *   root
 *   └ body            (bob / lean / recoil)
 *     ├ hips → legL, legR
 *     ├ torso
 *     │  ├ armL, armR  (each with a `hand` socket holding the weapon)
 *     │  └ head
 *     └ base           (the plinth the figure stands on)
 *
 * Rooks are the exception: the golem has no legs to speak of, but it still
 * exposes the same joint names (bound to its slab body) so animations that
 * reach for `legL` degrade gracefully instead of throwing.
 */

import * as THREE from 'three';
import { PALETTE, mat, metalMat, goldMat, clothMat, stoneMat, bronzeMat } from './materials.js';
import { part, partSized, prop, rig, rigReady } from './parts.js';

/* ------------------------------------------------------------------ */
/* Geometry helpers                                                    */
/*                                                                     */
/* Each falls back to a three.js primitive when the library is missing, */
/* so a failed asset load costs detail rather than the whole game.      */
/* ------------------------------------------------------------------ */

const box = (w, h, d, material) =>
  new THREE.Mesh(partSized('box', w, h, d) ?? new THREE.BoxGeometry(w, h, d), material);

/**
 * A rounded, softly tapered limb segment — arms, legs, horse legs.
 *
 * `name` selects which canonical segment to scale: 'limb' is bare, 'armPlate'
 * the same shape with lames banded into it. Both share the primitive fallback.
 */
const limb = (w, h, d, material, name = 'limb') =>
  new THREE.Mesh(partSized(name, w, h, d) ?? new THREE.CylinderGeometry(w / 2, d / 2, h, 10), material);

const cyl = (rt, rb, h, material, seg = 12) =>
  new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), material);

const sphere = (r, material, seg = 14) =>
  new THREE.Mesh(new THREE.SphereGeometry(r, seg, seg), material);

const cone = (r, h, material, seg = 12) =>
  new THREE.Mesh(new THREE.ConeGeometry(r, h, seg), material);

/** A bespoke Blender part at its authored size, with a primitive stand-in. */
const sculpted = (name, material, fallback) => {
  const g = part(name);
  return new THREE.Mesh(g ?? fallback(), material);
};

/**
 * Replaces an intact part with its broken state.
 *
 * `spec.shed` names the pieces that come away and `spec.stays` what is left
 * behind on the body — a battlement sheared in half, say, with two chunks
 * spalling off it. Every damaged part is authored around the same pivot as the
 * original, so copying the original's local transform lands them exactly where
 * it stood, and the caller only has to supply an impulse.
 *
 * The intact node is hidden rather than removed: it is far easier to restore,
 * which matters for the attacker, who survives the fight.
 */
export function damagePart(node, spec) {
  const parent = node?.parent;
  if (!parent || !spec) return null;

  let material = null;
  node.traverse((o) => { if (!material && o.isMesh) material = o.material; });
  if (!material) return null;

  const build = (entry) => {
    const name = typeof entry === 'string' ? entry : entry.part;
    const spec = typeof entry === 'string' ? {} : entry;
    const geometry = spec.size ? partSized(name, ...spec.size) : part(name);
    if (!geometry) return null;
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(node.position);
    mesh.quaternion.copy(node.quaternion);
    mesh.scale.copy(node.scale);
    // A break can put its pieces either side of the original — the two halves
    // of a snapped shaft sit above and below where it parted.
    if (spec.offset) mesh.position.add(new THREE.Vector3(...spec.offset).applyQuaternion(node.quaternion));
    if (spec.rotate) mesh.rotateX(spec.rotate);
    mesh.castShadow = true;
    parent.add(mesh);
    return mesh;
  };

  const shed = (spec.shed ?? []).map(build).filter(Boolean);
  // Nothing in the library matched, so leave the part whole rather than
  // making it vanish.
  if (!shed.length) return null;

  const stays = spec.stays ? build(spec.stays) : null;
  node.visible = false;
  return { shed, stays, hidden: node };
}

/** A pivot group — children hang below it so rotations swing like a joint. */
function joint(parent, x, y, z) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  parent.add(g);
  return g;
}

/**
 * Builds the plinth every piece stands on. The disc is what the raycaster
 * ultimately hits most often, so it is generous in radius.
 */
function buildBase(colors) {
  const base = new THREE.Group();
  // Deliberately darker than the figure: a bright plinth this wide upstages
  // the piece standing on it.
  const disc = sculpted('baseDisc', stoneMat(colors.plinth),
    () => new THREE.CylinderGeometry(0.30, 0.335, 0.055, 24));
  disc.position.y = 0.0275;
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(0.305, 0.020, 8, 24),
    metalMat(colors.accent)
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.055;
  base.add(disc, rim);
  return base;
}

/**
 * Shared humanoid armature. `opts` tunes the proportions so a pawn reads as a
 * wiry footsoldier and a king as a broad armoured monarch.
 */
function buildHumanoid(colors, opts = {}) {
  const {
    scale = 1,
    torsoColor = colors.cloth,
    legColor = colors.clothDark,
    armColor = colors.cloth,
    shoulderWidth = 0.16,
    torsoHeight = 0.3,
    legHeight = 0.28
  } = opts;

  const parts = {};
  const body = new THREE.Group();
  body.scale.setScalar(scale);

  const hips = joint(body, 0, legHeight + 0.07, 0);
  parts.hips = hips;

  const legGeomHeight = legHeight;
  for (const side of [-1, 1]) {
    const leg = joint(hips, side * 0.075, 0, 0);
    const thigh = limb(0.078, legGeomHeight, 0.088, clothMat(legColor));
    thigh.position.y = -legGeomHeight / 2;
    const boot = sculpted('boot', mat(colors.clothDark, { rough: 0.9 }),
      () => new THREE.BoxGeometry(0.09, 0.06, 0.13));
    boot.position.set(0, -legGeomHeight + 0.02, 0.015);
    leg.add(thigh, boot);
    parts[side < 0 ? 'legL' : 'legR'] = leg;
  }

  const torso = joint(hips, 0, 0, 0);
  parts.torso = torso;

  const chest = box(shoulderWidth * 2, torsoHeight, 0.14, clothMat(torsoColor));
  chest.position.y = torsoHeight / 2;
  const belt = box(shoulderWidth * 2.05, 0.04, 0.15, mat(colors.accent, { rough: 0.5, metal: 0.6 }));
  belt.position.y = 0.03;
  torso.add(chest, belt);

  for (const side of [-1, 1]) {
    const arm = joint(torso, side * (shoulderWidth + 0.016), torsoHeight - 0.035, 0);
    // A cap over the joint: without it the gap between chest and arm shows.
    const shoulder = new THREE.Mesh(
      partSized('sphere', 0.086, 0.076, 0.086) ?? new THREE.SphereGeometry(0.043, 10, 8),
      clothMat(armColor)
    );
    arm.add(shoulder);
    const upper = limb(0.07, 0.16, 0.066, clothMat(armColor));
    upper.position.y = -0.085;
    const fore = limb(0.064, 0.14, 0.06, mat(colors.skin, { rough: 0.7 }));
    fore.position.y = -0.22;
    arm.add(upper, fore);
    const hand = joint(arm, 0, -0.29, 0);
    const fist = sculpted('hand', mat(colors.skin, { rough: 0.7 }),
      () => new THREE.SphereGeometry(0.03, 8, 6));
    hand.add(fist);
    parts[side < 0 ? 'armL' : 'armR'] = arm;
    parts[side < 0 ? 'handL' : 'handR'] = hand;
  }

  const head = joint(torso, 0, torsoHeight + 0.115, 0);
  const skull = sculpted('head', mat(colors.skin, { rough: 0.65 }),
    () => new THREE.SphereGeometry(0.082, 14, 14));
  const neck = cyl(0.036, 0.045, 0.09, mat(colors.skin, { rough: 0.7 }), 8);
  neck.position.y = -0.085;
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(
      partSized('sphere', 0.022, 0.016, 0.014) ?? new THREE.SphereGeometry(0.011, 8, 6),
      mat(0x2b2118, { rough: 0.35 })
    );
    eye.position.set(side * 0.032, 0.008, 0.071);
    head.add(eye);
  }
  head.add(skull, neck);
  parts.head = head;

  return { body, parts };
}

/**
 * The chibi armature — a second skeleton, not a variant of the humanoid one.
 *
 * Every metric differs: the reference miniature is about 2.2 heads tall with
 * the helmeted head at nearly half that, while `buildHumanoid`'s arm alone is
 * 0.29 long — longer than this whole torso. Scaling the adult skeleton's head
 * up just produces a small adult, so the proportions have to be authored from
 * scratch.
 *
 * What makes that safe is that a builder's only contract is the *joint names*:
 * `resetPose` walks a fixed list and skips what is missing, `impactPoint` maps
 * 'chest' to `torso` and everything else by name, `damage.js` resolves slots
 * by name, and the editor lists them by name. So exposing the same names is
 * enough, and the five pieces built on `buildHumanoid` are untouched by this.
 */
function buildChibi(colors, opts = {}) {
  const {
    scale = 1,
    torsoColor = colors.clothDark,
    legColor = colors.clothDark,
    shoulderWidth = 0.10,
    torsoHeight = 0.17,
    legHeight = 0.12
  } = opts;

  const parts = {};
  const body = new THREE.Group();
  body.scale.setScalar(scale);

  const hips = joint(body, 0, legHeight + 0.045, 0);
  parts.hips = hips;

  // Stubby, wide-set legs ending in banded greaves.
  for (const side of [-1, 1]) {
    const leg = joint(hips, side * 0.055, 0, 0);
    const thigh = limb(0.085, legHeight, 0.090, clothMat(legColor));
    thigh.position.y = -legHeight / 2;
    const greave = sculpted('greave', bronzeMat(colors.metal),
      () => new THREE.BoxGeometry(0.088, 0.075, 0.125));
    greave.position.set(0, -legHeight + 0.010, 0.008);
    leg.add(thigh, greave);
    parts[side < 0 ? 'legL' : 'legR'] = leg;
    parts[side < 0 ? 'greaveL' : 'greaveR'] = greave;
  }

  const torso = joint(hips, 0, 0, 0);
  parts.torso = torso;
  // Kept shallow: on the pawn this is the padding under a breastplate, and
  // any deeper it pushes through the plate worn over it.
  const chest = box(shoulderWidth * 2, torsoHeight, 0.11, clothMat(torsoColor));
  chest.position.y = torsoHeight / 2;
  torso.add(chest);

  // Short arms — two banded segments, no elbow to speak of, ending in
  // gauntlets. Plate rather than sleeves: the arm runs continuously from the
  // pauldron above to the gauntlet below, as it does in the reference, and a
  // separate elbow couter would have nowhere to sit on an arm this short.
  for (const side of [-1, 1]) {
    const arm = joint(torso, side * (shoulderWidth + 0.012), torsoHeight - 0.028, 0);
    const upper = limb(0.056, 0.09, 0.054, bronzeMat(colors.metal), 'armPlate');
    upper.position.y = -0.048;
    const fore = limb(0.052, 0.08, 0.050, bronzeMat(colors.metal), 'armPlate');
    fore.position.y = -0.125;
    arm.add(upper, fore);
    const hand = joint(arm, 0, -0.170, 0);
    const gauntlet = sculpted('gauntlet', bronzeMat(colors.metal),
      () => new THREE.SphereGeometry(0.042, 10, 8));
    hand.add(gauntlet);
    parts[side < 0 ? 'armL' : 'armR'] = arm;
    parts[side < 0 ? 'handL' : 'handR'] = hand;
    parts[side < 0 ? 'gauntletL' : 'gauntletR'] = gauntlet;
  }

  // The head sits straight on the shoulders: a chibi figure has no neck, and
  // the sculpted skull is the face that shows through the helmet's opening.
  const head = joint(torso, 0, torsoHeight + 0.085, 0);
  // Skull and eyes travel together in a `face` group rather than sitting on
  // the joint directly, so a caller that puts a deeper helmet on can seat the
  // whole face back inside it with one number instead of three.
  const face = new THREE.Group();
  const skull = sculpted('head', mat(colors.skin, { rough: 0.65 }),
    () => new THREE.SphereGeometry(0.082, 14, 14));
  skull.scale.setScalar(0.88);
  // Set well forward, so the face nearly fills the helmet's opening. Left at
  // the centre of the head socket it sits ~30mm behind the rim and reads as a
  // dark hole rather than a face.
  skull.position.z = 0.042;
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(
      partSized('sphere', 0.019, 0.014, 0.012) ?? new THREE.SphereGeometry(0.0095, 8, 6),
      mat(0x2b2118, { rough: 0.35 })
    );
    eye.position.set(side * 0.025, 0.008, 0.108);
    face.add(eye);
  }
  face.add(skull);
  head.add(face);
  parts.head = head;
  parts.face = face;

  return { body, parts };
}

/* ------------------------------------------------------------------ */
/* Weapons and props                                                   */
/* ------------------------------------------------------------------ */

/**
 * An imported accessory out of `props.glb`, painted, or null if it is absent.
 *
 * Deliberately not pawn-specific: the king's sword and the knight's shield are
 * the same graft onto the same joints, and this is what they would call.
 *
 * The material must be assigned here rather than left to the file — the
 * importer exports with `export_materials='NONE'`, so a prop arrives wearing
 * three.js's default white, and `clone(true)` shares material references.
 * Sizing and orientation are baked into the geometry by the importer, so the
 * returned node is at identity scale, which is what keeps the debris that
 * `damagePart` builds from inheriting a scale factor.
 */
function equip(name, material) {
  const node = prop('props', name);
  if (!node) return null;
  node.traverse((o) => { if (o.isMesh) o.material = material; });
  return node;
}

/**
 * The three helmets a pawn can draw, with where each one seats its plume.
 *
 * Ordered, because `session.js` deals an index into this table and the helmet
 * has to stay put for the length of a game.
 *
 * Nothing here can be one shared number. Crown heights differ by 70mm across
 * the three, so `plumeY`/`plumeScale` are per helmet; and `faceZ` seats the
 * skull at the depth each opening wants — furthest back behind the knight's
 * wide visor slot, closer up behind the barrel helm's small eye holes.
 */
export const HELMETS = [
  // The knight's helm is the one that already has a finial. Rather than stack
  // a crest on a spike, the plume is seated at the spike's base and sized to
  // sheathe it, so the army colour lands where the eye already expects a point.
  { name: 'helmKnight', y: 0.004, plumeY: 0.147, plumeScale: 0.70, faceZ: -0.052 },
  { name: 'helmBarrel', y: 0.026, plumeY: 0.199, plumeScale: 0.80, faceZ: -0.030 },
  { name: 'helmFencing', y: 0.010, plumeY: 0.195, plumeScale: 0.80, faceZ: -0.040 }
];

function makeSpear(colors, length = 0.62) {
  const g = new THREE.Group();
  const shaft = cyl(0.014, 0.014, length, mat(0x6b4a2c, { rough: 0.9 }), 8);
  const tip = sculpted('spearTip', metalMat(colors.metal),
    () => new THREE.ConeGeometry(0.035, 0.12, 12));
  tip.position.y = length / 2 + 0.055;
  g.add(shaft, tip);
  return g;
}

/** `bladeMat`/`fittingsMat` default to the king's steel-and-gold; the pawn
 * passes bronzeMat for both so its whole blade reads as cast, not forged. */
function makeSword(colors, length = 0.4, bladeMat = metalMat, fittingsMat = goldMat) {
  const g = new THREE.Group();
  // The Blender blade carries its own point, so there is no separate tip cone.
  const blade = new THREE.Mesh(
    partSized('swordBlade', 1, length, 1) ?? new THREE.BoxGeometry(0.045, length, 0.014),
    bladeMat(colors.metal)
  );
  blade.position.y = length / 2 + 0.04;
  const guard = sculpted('swordGuard', fittingsMat(colors.accent),
    () => new THREE.BoxGeometry(0.15, 0.022, 0.03));
  guard.position.y = 0.04;
  const grip = cyl(0.018, 0.018, 0.09, mat(0x3a2a1c, { rough: 0.9 }), 8);
  grip.position.y = -0.02;
  const pommel = sculpted('pommel', fittingsMat(colors.accent),
    () => new THREE.SphereGeometry(0.028, 10, 10));
  pommel.position.y = -0.07;
  g.add(blade, guard, grip, pommel);
  return g;
}

function makeShield(colors, plateMat = metalMat) {
  const g = new THREE.Group();
  // A heater shield: already curved and bossed, and it faces +Z as authored.
  const plate = part('shield');
  if (plate) {
    g.add(new THREE.Mesh(plate, plateMat(colors.metal)));
  } else {
    const disc = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.1, 0.025, 16), plateMat(colors.metal));
    disc.rotation.x = Math.PI / 2;
    const boss = sphere(0.035, goldMat(colors.accent), 10);
    boss.position.z = 0.02;
    g.add(disc, boss);
  }
  return g;
}

function makeStaff(colors, length = 0.7) {
  const g = new THREE.Group();
  const shaft = cyl(0.016, 0.018, length, mat(0x4a3520, { rough: 0.9 }), 8);
  const orb = sphere(0.055, mat(colors.accent, { rough: 0.2, metal: 0.3, emissive: colors.accent }), 14);
  orb.position.y = length / 2 + 0.05;
  const claw = sculpted('staffClaw', goldMat(colors.accent),
    () => new THREE.TorusGeometry(0.055, 0.012, 6, 12, Math.PI * 1.4));
  claw.position.y = length / 2 + 0.05;
  g.add(shaft, orb, claw);
  g.userData.orb = orb;
  return g;
}

function makeCrown(colors, radius = 0.09, points = 5) {
  const crown = new THREE.Group();
  const band = new THREE.Mesh(
    partSized('crownBand', radius * 2, 0.045, radius * 2) ??
      new THREE.CylinderGeometry(radius, radius, 0.045, 16, 1, true),
    goldMat(colors.accent)
  );
  crown.add(band);
  for (let i = 0; i < points; i++) {
    const a = (i / points) * Math.PI * 2;
    const spike = sculpted('crownSpike', goldMat(colors.accent),
      () => new THREE.ConeGeometry(0.022, 0.075, 6));
    spike.position.set(Math.cos(a) * radius, 0.055, Math.sin(a) * radius);
    const gem = sphere(0.016, mat(colors.accent, { rough: 0.15, metal: 0.2, emissive: 0x220000 }), 8);
    gem.position.set(Math.cos(a) * radius, 0.02, Math.sin(a) * radius);
    crown.add(spike, gem);
  }
  return crown;
}

function makeCape(colors, width = 0.34, length = 0.42) {
  // The modelled cape has thickness and a wind-caught curl; the curl scales
  // with length so a long royal cape billows further than a short one.
  const g = partSized('cape', width, length, length);
  const cape = new THREE.Mesh(
    g ?? new THREE.PlaneGeometry(width, length, 4, 6),
    new THREE.MeshStandardMaterial({
      color: colors.accent,
      roughness: 0.9,
      side: g ? THREE.FrontSide : THREE.DoubleSide
    })
  );
  cape.position.set(0, -length / 2 + 0.10, -0.085);
  return cape;
}

/* ------------------------------------------------------------------ */
/* Per-type builders                                                   */
/* ------------------------------------------------------------------ */

/**
 * The pawn: a chibi man-at-arms, assembled from the parts the Neko Miniatures
 * reference diagram labels — bascinet and face plate, swept plume, riveted
 * cuirass, pauldrons, mail skirt, gauntlets, greaves, sword and shield.
 *
 * The finish is aged bronze over each army's own `metal` hue rather than a
 * warm bronze colour, so the two sides stay as tellable apart as the knight
 * and king are, at the cost of not matching the reference's brown.
 */
function buildPawn(colors, variant = 0) {
  // Chibi proportions make a short figure; without this the pawn rank reads as
  // markedly smaller than the pieces behind it rather than merely stouter.
  const { body, parts } = buildChibi(colors, { scale: 1.12 });

  // Which helmet this pawn drew. The name is the contract, not the shape:
  // whichever of the three is rolled, it is `parts.helm`, so the damage table,
  // resetPose and the editor all still find it.
  const helmet = HELMETS[variant % HELMETS.length];
  const helm = equip(helmet.name, bronzeMat(colors.metal));
  if (helm) {
    helm.position.y = helmet.y;
    parts.head.add(helm);
    parts.helm = helm;
    // The face was set forward to fill the old dome's wide opening. These
    // helmets have real visors and eye slots, so a face left that far out
    // presses through them and reads as a blank pale mask rather than as
    // someone looking out of a helmet. Seat it back into the shadow.
    parts.face.position.z = helmet.faceZ;
  } else {
    // No props file: the pawn exactly as it was built before the sculpts
    // arrived, face plate included. Same non-fatal fallback every other
    // imported model here has.
    const dome = sculpted('helmChibi', bronzeMat(colors.metal),
      () => new THREE.SphereGeometry(0.125, 16, 12));
    dome.position.y = 0.008;
    parts.head.add(dome);
    parts.helm = dome;

    const facePlate = sculpted('facePlate', bronzeMat(colors.metal),
      () => new THREE.SphereGeometry(0.105, 12, 10));
    parts.head.add(facePlate);
    parts.facePlate = facePlate;
  }

  // The accent colour on the plume is most of what tells the armies apart at
  // board scale, since the armour itself is only a finish away from grey. Its
  // seat is per-helmet: the three sculpts differ by 70mm in crown height, and
  // one of them already carries a finial of its own to clear.
  const plume = sculpted('helmPlume', bronzeMat(colors.accent),
    () => new THREE.ConeGeometry(0.032, 0.15, 8));
  plume.position.y = helm ? helmet.plumeY : 0.070;
  if (helm) plume.scale.setScalar(helmet.plumeScale);
  parts.head.add(plume);
  parts.plume = plume;

  // The cuirass straps over the cloth torso rather than replacing it.
  const cuirass = sculpted('cuirassChibi', bronzeMat(colors.metal),
    () => new THREE.BoxGeometry(0.21, 0.19, 0.15));
  cuirass.position.y = 0.082;
  parts.torso.add(cuirass);
  parts.cuirass = cuirass;

  // The adult figures' pauldron, scaled up for chibi bulk. `damagePart`
  // copies the node's scale onto the replacement it builds, so the cracked
  // state inherits this without a second Blender part.
  for (const side of [-1, 1]) {
    const pauldron = sculpted('pauldron', bronzeMat(colors.metal), () => {
      const g = new THREE.SphereGeometry(0.075, 10, 10);
      g.scale(1, 0.7, 1);
      return g;
    });
    pauldron.scale.setScalar(1.02);
    pauldron.position.set(side * 0.104, 0.150, 0);
    parts.torso.add(pauldron);
    parts[side < 0 ? 'pauldronL' : 'pauldronR'] = pauldron;
  }

  const skirt = sculpted('mailSkirt', bronzeMat(colors.clothDark),
    () => new THREE.CylinderGeometry(0.098, 0.132, 0.115, 16));
  // Set high, not low: the cuirass is widest above the fauld, so tucking the
  // skirt up there hides its top rim. Hung lower, the rim clears the narrowing
  // plate and its upward-facing cap catches the key light as a bright shelf.
  skirt.position.y = -0.008;
  parts.hips.add(skirt);
  parts.skirt = skirt;

  const importedSword = equip('wornSword', bronzeMat(colors.metal));
  const sword = importedSword ?? makeSword(colors, 0.26, bronzeMat, bronzeMat);
  if (importedSword) {
    // Placement tuned by hand in the model editor and folded in as the
    // default, on top of the grip-relative origin import_props.py baked in.
    sword.position.set(0.002, 0.04, 0.044);
    sword.rotation.set(0.61, 1.61, -0.35);
    sword.scale.set(1.21, 1.56, 1);
  } else {
    sword.position.y = -0.02;
    // Canted outward, away from the body. Held vertical the blade rises
    // directly in front of the helm and disappears into it — a grey blade on
    // grey armour has no silhouette to read against.
    sword.rotation.z = -0.30;
  }
  parts.handR.add(sword);
  parts.weapon = sword;

  const imported = equip('medievalShield', bronzeMat(colors.metal));
  const shield = imported ?? makeShield(colors, bronzeMat);
  if (imported) {
    // Same: hand-tuned in the editor, folded in as the default.
    shield.position.set(-0.029, 0.064, 0.062);
    shield.rotation.set(0, -0.89, 0.42);
  } else {
    shield.scale.setScalar(0.92);
    shield.rotation.y = -0.30;
    shield.position.set(-0.015, -0.012, 0.050);
  }
  parts.handL.add(shield);
  parts.shield = shield;

  return { body, parts, height: 0.56 };
}

function buildKnightPrimitive(colors) {
  const group = new THREE.Group();
  const parts = {};

  // The mount: a stylised warhorse with the rider seated on its back.
  const horse = new THREE.Group();
  const barrel = sculpted('horseBarrel', mat(colors.stone, { rough: 0.8 }), () => {
    const g = new THREE.CapsuleGeometry(0.115, 0.24, 6, 12);
    g.rotateX(Math.PI / 2);
    return g;
  });
  barrel.position.y = 0.36;
  horse.add(barrel);

  const neck = cyl(0.06, 0.085, 0.24, mat(colors.stone, { rough: 0.8 }), 10);
  neck.position.set(0, 0.5, 0.14);
  neck.rotation.x = -0.55;
  const skull = sculpted('horseHead', mat(colors.stone, { rough: 0.8 }),
    () => new THREE.BoxGeometry(0.09, 0.1, 0.2));
  skull.position.set(0, 0.585, 0.255);
  skull.rotation.x = 0.35;
  const mane = sculpted('mane', mat(colors.accent, { rough: 0.9 }),
    () => new THREE.BoxGeometry(0.03, 0.1, 0.22));
  mane.position.set(0, 0.565, 0.115);
  mane.rotation.x = -0.5;
  horse.add(neck, skull, mane);

  for (const [dx, dz] of [[-0.08, 0.11], [0.08, 0.11], [-0.08, -0.11], [0.08, -0.11]]) {
    const leg = new THREE.Mesh(
      partSized('horseLeg', 0.064, 0.3, 0.064) ?? new THREE.CylinderGeometry(0.032, 0.026, 0.3, 8),
      mat(colors.stone, { rough: 0.85 })
    );
    leg.position.set(dx, 0.16, dz);
    const hoof = sculpted('horseHoof', mat(colors.clothDark, { rough: 0.9 }),
      () => new THREE.CylinderGeometry(0.034, 0.036, 0.04, 8));
    hoof.position.set(dx, 0.02, dz);
    horse.add(leg, hoof);
  }

  const tail = cone(0.045, 0.22, mat(colors.accent, { rough: 0.9 }), 8);
  tail.position.set(0, 0.38, -0.19);
  tail.rotation.x = 0.9;
  horse.add(tail);

  const caparison = box(0.24, 0.16, 0.28, clothMat(colors.cloth));
  caparison.position.y = 0.33;
  horse.add(caparison);
  group.add(horse);
  parts.mount = horse;

  // The rider reuses the humanoid rig, scaled down and seated.
  const rider = buildHumanoid(colors, {
    scale: 0.62,
    torsoColor: colors.metal,
    armColor: colors.metal,
    legColor: colors.metal,
    shoulderWidth: 0.15
  });
  rider.body.position.y = 0.42;
  // Order matters here: 'ZXY' applies the outward straddle before the
  // forward lean, so the two compose the way they read visually. The
  // default 'XYZ' order applies the straddle last, twisting it around the
  // leg's already-tilted axis instead — that sent the legs jutting forward
  // past the horse's head rather than tucking under the rider.
  rider.parts.legL.rotation.set(-0.62, 0, -0.30, 'ZXY');
  rider.parts.legR.rotation.set(-0.62, 0, 0.30, 'ZXY');
  group.add(rider.body);

  const helm = sculpted('helmGreat', metalMat(colors.metal),
    () => new THREE.CylinderGeometry(0.085, 0.095, 0.11, 10));
  helm.position.y = 0.015;
  const plume = cone(0.026, 0.14, mat(colors.accent, { rough: 0.9 }), 8);
  plume.position.y = 0.155;
  rider.parts.head.add(helm, plume);

  const lance = makeSpear(colors, 0.8);
  lance.position.set(0, -0.02, 0.12);
  lance.rotation.x = -1.15;
  rider.parts.handR.add(lance);

  const shield = makeShield(colors);
  shield.rotation.y = -0.45;
  shield.position.set(-0.03, -0.05, 0.04);
  rider.parts.handL.add(shield);

  // rider.parts is merged in last, so the knight's own handles are named here
  // rather than where they are built.
  Object.assign(parts, rider.parts, {
    weapon: lance, shield, rider: rider.body, helm, plume, caparison, horseHead: skull
  });
  return { body: group, parts, height: 0.95 };
}

/**
 * The knight is the one piece built from a sculpt rather than from primitives.
 *
 * `knight.glb` carries no materials by design: the game paints both armies from
 * its own palette, so the same file serves white and black and the sculpt sits
 * in the same world as the other five ranks instead of looking imported. Each
 * node is given the material its real counterpart would have — plate on the
 * rider, mail and plate on the horse's crinet and chanfron, cloth on the
 * caparison and tail, ash on the lance.
 */
const KNIGHT_SURFACE = {
  metal: ['helm', 'torso', 'armL', 'armR', 'handL', 'handR', 'legL', 'legR',
          'neck', 'horseHead'],
  cloth: ['caparison'],
  accent: ['tail'],
  hide: ['barrel', 'rump', 'legFL', 'legFR', 'legBL', 'legBR'],
  wood: ['weapon']
};

function buildKnight(colors) {
  const built = rigReady('knight') ? rig('knight') : null;
  if (!built) return buildKnightPrimitive(colors);

  const surface = new Map();
  for (const [kind, names] of Object.entries(KNIGHT_SURFACE)) {
    const material =
      kind === 'metal' ? metalMat(colors.metal)
      : kind === 'cloth' ? clothMat(colors.cloth)
      : kind === 'accent' ? clothMat(colors.accent)
      : kind === 'wood' ? mat(colors.clothDark, { rough: 0.8, metal: 0.05 })
      : mat(colors.stone, { rough: 0.85, metal: 0.05 });
    for (const name of names) surface.set(name, material);
  }

  const parts = {};
  for (const [name, node] of built.nodes) {
    parts[name] = node;
    const material = surface.get(name);
    if (material) node.traverse((o) => { if (o.isMesh) o.material = material; });
  }

  // The sculpt's left hand holds the reins; the shield is grafted onto that
  // arm, where a knight would carry it, so the damage table's `shield` slot
  // still has something to lose.
  const shield = makeShield(colors);
  shield.scale.setScalar(0.85);
  shield.rotation.set(0.15, -0.5, 0.2);
  shield.position.set(0.055, -0.02, -0.03);
  (parts.handL ?? built.root).add(shield);
  parts.shield = shield;

  // `rider` doubles as the humanoid body handle the animator expects.
  parts.rider = parts.rider ?? built.root;

  const group = new THREE.Group();
  group.add(built.root);
  return { body: group, parts, height: 0.95, rigged: true };
}


function buildBishopPrimitive(colors) {
  const { body, parts } = buildHumanoid(colors, {
    scale: 0.95,
    torsoColor: colors.cloth,
    armColor: colors.cloth,
    legColor: colors.cloth,
    torsoHeight: 0.3
  });

  // A full robe hides the legs, so the mage glides rather than walks.
  const robe = sculpted('robe', clothMat(colors.cloth),
    () => new THREE.CylinderGeometry(0.13, 0.26, 0.44, 16));
  robe.position.y = 0.22;
  body.add(robe);
  parts.robe = robe;
  const trim = new THREE.Mesh(
    new THREE.TorusGeometry(0.255, 0.02, 6, 20),
    mat(colors.accent, { rough: 0.6, metal: 0.4 })
  );
  trim.rotation.x = Math.PI / 2;
  trim.position.y = 0.03;
  body.add(trim);

  const mitre = sculpted('mitre', clothMat(colors.accent),
    () => new THREE.ConeGeometry(0.085, 0.22, 10));
  mitre.position.y = 0.16;
  const mitreBand = new THREE.Mesh(
    new THREE.TorusGeometry(0.075, 0.014, 6, 14),
    goldMat(colors.accent)
  );
  mitreBand.rotation.x = Math.PI / 2;
  mitreBand.position.y = 0.07;
  parts.head.add(mitre, mitreBand);
  parts.mitre = mitre;

  const beard = sculpted('beard', mat(0xd8d8d0, { rough: 0.95 }),
    () => new THREE.ConeGeometry(0.055, 0.14, 8));
  beard.position.set(0, -0.09, 0.045);
  beard.rotation.x = Math.PI;
  parts.head.add(beard);
  parts.beard = beard;

  const staff = makeStaff(colors);
  staff.position.y = -0.12;
  parts.handR.add(staff);
  parts.weapon = staff;
  parts.orb = staff.userData.orb;

  return { body, parts, height: 0.98 };
}

/**
 * A book that opens and closes.
 *
 * The bishop's sculpt carries its own book splayed open and fused to both
 * hands, so it cannot be shut — this replaces it. Each cover is authored in
 * Blender reaching away from its own origin, and the origin sits on the spine,
 * so opening and closing is one rotation per cover about the hinge and nothing
 * has to be moved. `userData.covers` is what the animator swings.
 */
function makeBook(colors) {
  const group = new THREE.Group();
  // Gilt rather than leather-and-paper: a book this ornate reads as the
  // bishop's real weapon rather than a prop. One material for the whole
  // book, covers and leaves alike, rather than two — a design pass through
  // the model editor settled on treating it as a single gold-plated object.
  const gilt = mat(colors.accent, { rough: 0.30, metal: 0.85 });

  // The boards are authored upright, reaching along +X from a spine on the
  // origin. Laying them down here — rather than in Blender — keeps the library
  // part orientation-neutral, and means the hinge is a rotation about Z: the
  // axis the spine runs along once the book is flat.
  const board = (geo, material, thin) => {
    const mesh = sculpted(geo, material,
      () => new THREE.BoxGeometry(thin ? 0.106 : 0.115, thin ? 0.141 : 0.150,
                                  thin ? 0.007 : 0.009));
    mesh.rotation.x = Math.PI / 2;
    return mesh;
  };

  const spine = sculpted('bookSpine', gilt,
    () => new THREE.CylinderGeometry(0.008, 0.008, 0.150, 8));
  spine.rotation.x = Math.PI / 2;
  group.add(spine);

  // The right board never moves; the left one swings over onto it. That is how
  // a book actually shuts, and it costs one keyframed rotation instead of two.
  const covers = [];
  for (const side of [0, 1]) {
    const hinge = new THREE.Group();
    const cover = board('bookCover', gilt, false);
    const leaves = board('bookLeaves', gilt, true);
    cover.position.y = side ? 0.006 : -0.006;
    leaves.position.y = side ? 0.002 : -0.002;
    hinge.add(cover, leaves);
    group.add(hinge);
    covers.push(hinge);
  }
  group.userData.covers = covers;
  setBookOpen(group, 1);
  return group;
}

/** 0 is shut, 1 is lying open. Driven from the animator. */
export function setBookOpen(book, t) {
  const covers = book.userData.covers ?? [];
  // Shut, both boards lie on top of each other reaching the same way; open,
  // one has swung a half turn to the other side of the spine.
  if (covers[1]) covers[1].rotation.z = t * Math.PI;
}

const BISHOP_SURFACE = {
  cloth: ['robe', 'torso', 'armR', 'armL'],
  accent: ['mitre'],
  // Cut from the same welded surface as the face (see import_bishop.py's
  // classify()) and painted separately, the same fix and the same palette
  // key as the king's hairBeard.
  hair: ['hair'],
  skin: ['head', 'handR', 'handL']
};

function buildBishop(colors) {
  const built = rigReady('bishop') ? rig('bishop') : null;
  if (!built) return buildBishopPrimitive(colors);

  const surface = new Map();
  for (const [kind, names] of Object.entries(BISHOP_SURFACE)) {
    const material =
      kind === 'cloth' ? clothMat(colors.cloth)
      : kind === 'accent' ? clothMat(colors.accent)
      : kind === 'hair' ? clothMat(colors.hair)
      : mat(colors.skin, { rough: 0.7 });
    for (const name of names) surface.set(name, material);
  }

  const parts = {};
  for (const [name, node] of built.nodes) {
    parts[name] = node;
    const material = surface.get(name);
    if (material) node.traverse((o) => { if (o.isMesh) o.material = material; });
  }

  // The sculpt's book was cut away in Blender; this one can shut. It hangs off
  // the right hand so that swinging `armR` carries it, which is what the slap
  // is made of — the left hand only steadies it.
  const book = makeBook(colors);
  // Sized and placed by the same editor pass: larger and carried a touch
  // lower and further forward than the first cut, so it reads clearly in
  // both hands instead of being lost against the robe.
  book.position.set(0.04, 0.012, 0.12);
  book.scale.set(1.08, 1.31, 1.2);
  book.rotation.set(0, 0, 0);
  (parts.handR ?? built.root).add(book);
  parts.book = book;
  parts.weapon = book;

  const group = new THREE.Group();
  group.add(built.root);
  return { body: group, parts, height: 0.98, rigged: true };
}


/**
 * Where the gun sits when it is stowed behind the port, and how far it runs
 * out to fire. Exported so the artillery set piece keyframes between the two
 * without duplicating the numbers.
 */
// Run-out is measured so the barrel clears the drum wall (radius 0.142) by a
// visible margin rather than peeping out of it: the muzzle ends up around
// z 0.23, a good 90mm proud of the masonry. Stowed is pulled back far enough
// that the muzzle sits just behind the port instead of poking through the
// wall — at the old -0.02 it still protruded about 40mm even "hidden", which
// is what showed up as a barrel sticking out past the dark rectangle
// whenever something (the editor's visibility toggle) made the cannon
// visible at rest.
export const CANNON_STOWED = -0.08;
export const CANNON_RUN_OUT = 0.140;

/**
 * Puts the gun away: back behind the port, hidden, level, and with the muzzle
 * flash snuffed out.
 *
 * The counterpart to the artillery set piece, and the reason resetPose calls
 * it after every move — exactly the pairing `setBookOpen` has with the
 * bishop's slap. Without it a castle that has fired once keeps its gun run out
 * for the rest of the game.
 */
export function stowCannon(cannon) {
  if (!cannon) return;
  cannon.visible = false;
  cannon.position.z = CANNON_STOWED;
  if (cannon.userData.barrel) cannon.userData.barrel.rotation.x = Math.PI / 2;
  if (cannon.userData.flash) cannon.userData.flash.scale.setScalar(0.0001);
}

/**
 * The rook: a siege tower, after the Neko Miniatures exploded battle rook.
 *
 * A battered base with a breachable gate, an interlocking mid segment that
 * hides the artillery, and a detachable crenellated cap. It is dismantled
 * rather than beaten: every named part above is one the damage inventory can
 * take off, in the order the reference's own teardown shows.
 *
 * It has no legs and no arms. `hips`/`legL`/`legR` alias onto the body so an
 * animation that reaches for a leg poses something harmless instead of
 * throwing, and `head` sits at the battlement so head-tracking still has
 * somewhere to aim — the same contract the golem kept.
 */
function buildRook(colors) {
  const group = new THREE.Group();
  const parts = {};

  const torso = joint(group, 0, 0, 0);
  parts.torso = torso;
  parts.hips = torso;
  parts.legL = torso;
  parts.legR = torso;

  const stone = stoneMat(colors.stone);

  const base = sculpted('towerBase', stone,
    () => new THREE.BoxGeometry(0.34, 0.32, 0.34));
  base.position.y = 0.18;
  torso.add(base);
  parts.base = base;

  const segment = sculpted('towerSegment', stone,
    () => new THREE.CylinderGeometry(0.130, 0.142, 0.30, 14));
  segment.position.y = 0.50;
  torso.add(segment);
  parts.segment = segment;

  const cap = sculpted('battlementCap', stone,
    () => new THREE.CylinderGeometry(0.170, 0.148, 0.078, 16));
  cap.position.y = 0.715;
  torso.add(cap);
  parts.cap = cap;

  // Crenellations ring the parapet, and hang off the cap rather than the body:
  // the inventory knocks them off one at a time, and whichever are still
  // standing when the cap itself is struck off go with it.
  parts.merlons = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const merlon = new THREE.Mesh(
      partSized('merlon', 0.050, 0.070, 0.050) ?? new THREE.BoxGeometry(0.050, 0.070, 0.050),
      stone
    );
    merlon.position.set(Math.cos(a) * 0.150, 0.088, Math.sin(a) * 0.150);
    merlon.rotation.y = -a;
    cap.add(merlon);
    parts.merlons.push(merlon);
  }

  // The gate: a dark opening with a plank door hung in it. The door is its own
  // part, so staving it in leaves the opening behind — which is exactly what
  // the reference's dismantled state shows.
  // Deliberately a shade smaller than the door hung over it, so the doorway
  // shows only once the door has been staved in.
  const opening = box(0.098, 0.138, 0.02, mat(0x14120f, { rough: 1 }));
  opening.position.set(0, 0.135, 0.154);
  torso.add(opening);
  const door = sculpted('gateDoor', mat(0x6b4a2c, { rough: 0.9 }),
    () => new THREE.BoxGeometry(0.115, 0.150, 0.016));
  door.position.set(0, 0.133, 0.168);
  torso.add(door);
  parts.gate = door;

  // The artillery port: an embrasure rather than a window, with the furnace
  // banked behind it. Set deep on purpose — the gun is what comes out of it,
  // so the slot wants to read as dark with something alight further in, not
  // as a lit panel on the wall.
  // Sized and centred to frame the whole mechanism (barrel, carriage, wheels)
  // when it's visible at rest, not just the muzzle — measured against the
  // cannon group's own bounding box (roughly x ±0.05, y 0.42-0.53) with a
  // margin, rather than guessed.
  const port = box(0.14, 0.15, 0.03, mat(0x14120f, { rough: 1 }));
  port.position.set(0, 0.473, 0.135);
  torso.add(port);
  const core = sphere(0.026, mat(colors.accent, { rough: 0.4, emissive: colors.accent }), 12);
  core.position.set(0, 0.503, 0.104);
  torso.add(core);
  parts.core = core;

  const cannon = new THREE.Group();
  const carriage = sculpted('cannonCarriage', bronzeMat(colors.metal),
    () => new THREE.BoxGeometry(0.062, 0.030, 0.125));
  const barrel = sculpted('cannonBarrel', bronzeMat(colors.metal),
    () => new THREE.CylinderGeometry(0.021, 0.031, 0.19, 12));
  // Authored standing up the Y axis like every bespoke part; laid level so it
  // points out through the port.
  barrel.rotation.x = Math.PI / 2;
  barrel.position.y = 0.042;
  // The flash lives on the barrel so it swings with the elevation, and sits
  // at the muzzle. Kept at zero scale rather than hidden, because a scale is
  // something a keyframe can drive and `visible` is not.
  // Sits along the barrel's own +Y, which is the bore: the barrel is already
  // laid level, and the flash inherits that, so it needs no rotation of its own.
  // Long and narrow, and set clear of the mouth: a short fat cone sits over
  // the barrel and hides the very thing it is meant to be coming out of.
  const flash = cone(0.042, 0.19, mat(0xffb457, { rough: 0.4, emissive: 0xff8a2a }), 10);
  flash.position.y = 0.215;
  flash.scale.setScalar(0.0001);
  barrel.add(flash);
  cannon.add(carriage, barrel);
  cannon.position.set(0, 0.455, CANNON_STOWED);
  cannon.visible = false;
  // Handles for the artillery set piece and for stowing it again afterwards.
  cannon.userData.barrel = barrel;
  cannon.userData.flash = flash;
  cannon.userData.muzzle = flash;
  torso.add(cannon);
  parts.cannon = cannon;
  parts.cannonBarrel = barrel;
  parts.cannonCarriage = carriage;
  parts.muzzleFlash = flash;

  // Leaning against the base, as the diagram has it — detached, and the first
  // thing to clatter off when the tower is hit. Pose baked in from a model
  // editor pass: the steeper pitch and twist is what separates the two rails
  // enough to read as a ladder rather than a single leaning stick.
  const ladder = sculpted('siegeLadder', mat(0x6b4a2c, { rough: 0.9 }),
    () => new THREE.BoxGeometry(0.085, 0.34, 0.02));
  ladder.position.set(0.183, 0.17, 0.003);
  ladder.scale.set(0.97, 1.02, 1.1);
  ladder.rotation.set(-0.07, 1.49, 0.07);
  torso.add(ladder);
  parts.ladder = ladder;

  parts.head = joint(torso, 0, 0.76, 0);

  return { body: group, parts, height: 0.88 };
}

// Which palette hue each node of the sculpt wears. Same table shape as
// KING_SURFACE/BISHOP_SURFACE, painted the same way for the same reason —
// see blender/import_queen.py's own header for why the texture is dropped.
const QUEEN_SURFACE = {
  royal: ['torso', 'trim', 'armL', 'armR', 'shoulderCapL', 'shoulderCapR'],
  accent: ['crown'],
  hair: ['hair'],
  cloth: ['footL', 'footR', 'shoeTrimL', 'shoeTrimR'],
  skin: ['head', 'legs']
};

// Cut from the same atlas as `head` (import_queen.py's face_region()) but
// painted as fixed literals, not from PALETTE: a painted lip is the same
// colour on both armies, the way a real miniature's face paint doesn't
// change with its side of the board.
const QUEEN_MAKEUP = {
  lips: 0xc23a30,
  browLash: 0x2a2018
};

function buildQueenSculpt(colors) {
  const built = rig('queen');

  const surface = new Map();
  for (const [kind, names] of Object.entries(QUEEN_SURFACE)) {
    const material =
      kind === 'royal' ? clothMat(colors.royal)
      : kind === 'accent' ? goldMat(colors.accent)
      : kind === 'hair' ? clothMat(colors.hair)
      : kind === 'cloth' ? clothMat(colors.cloth)
      : mat(colors.skin, { rough: 0.7 });
    for (const name of names) surface.set(name, material);
  }

  const parts = {};
  for (const [name, node] of built.nodes) {
    parts[name] = node;
    const material = surface.get(name);
    if (material) {
      node.traverse((o) => {
        if (o.isMesh && (o === node || !surface.has(o.name))) o.material = material;
      });
    }
  }

  for (const [name, hex] of Object.entries(QUEEN_MAKEUP)) {
    parts[name]?.traverse((o) => {
      if (o.isMesh) o.material = mat(hex, { rough: name === 'lips' ? 0.4 : 0.6 });
    });
  }

  // The sculpt has no separate hand nodes — the forearm+hand shell is one
  // fused island in the source, the same shape as the rook's legless torso
  // (pieces.js header) — so hands alias onto the arms they end in.
  parts.handR = parts.handR ?? parts.armR;
  parts.handL = parts.handL ?? parts.armL;

  // The rest pose, tuned by hand in the model editor and folded in here as
  // the default rather than left as a saved overlay: nudges to the rig's own
  // authored arm/leg/foot positions, on top of whatever import_queen.py set.
  parts.armL.position.add(new THREE.Vector3(-0.012, 0.05, -0.006));
  parts.armR.position.add(new THREE.Vector3(0, 0.038, 0));
  parts.legs.position.add(new THREE.Vector3(-0.004, 0.032, 0));
  parts.footL.position.add(new THREE.Vector3(0.024, 0.042, 0));
  parts.footR.position.add(new THREE.Vector3(-0.024, 0.038, 0));

  const staff = makeStaff(colors, 0.5);
  staff.position.set(-0.03, -0.102, 0.188);
  staff.rotation.set(1.7, 0.23, -0.18);
  parts.handR.add(staff);
  parts.weapon = staff;
  parts.orb = staff.userData.orb;

  const group = new THREE.Group();
  group.add(built.root);
  // Measured, not guessed: her soles sat ~0.09 units above the plinth's top
  // surface (world scale) — a visible gap the other sculpts don't have.
  group.position.y -= 0.07;
  return { body: group, parts, height: 0.95, rigged: true };
}

function buildQueen(colors) {
  if (rigReady('queen')) return buildQueenSculpt(colors);
  const { body, parts } = buildHumanoid(colors, {
    scale: 0.98,
    torsoColor: colors.cloth,
    armColor: colors.cloth,
    shoulderWidth: 0.13,
    torsoHeight: 0.3
  });

  const gown = sculpted('gown', clothMat(colors.cloth),
    () => new THREE.CylinderGeometry(0.12, 0.24, 0.42, 16));
  gown.position.y = 0.21;
  body.add(gown);
  parts.gown = gown;
  const sash = box(0.06, 0.3, 0.02, mat(colors.accent, { rough: 0.7 }));
  sash.position.set(0, 0.15, 0.13);
  parts.torso.add(sash);
  parts.sash = sash;

  const hair = sculpted('hair', mat(colors.clothDark, { rough: 0.9 }),
    () => new THREE.SphereGeometry(0.095, 12, 12));
  hair.position.y = 0.015;
  parts.head.add(hair);
  parts.hair = hair;

  const crown = makeCrown(colors, 0.088, 6);
  crown.position.y = 0.085;
  parts.head.add(crown);
  parts.crown = crown;

  const staff = makeStaff(colors, 0.58);
  staff.position.y = -0.1;
  parts.handR.add(staff);
  parts.weapon = staff;
  parts.orb = staff.userData.orb;

  const cape = makeCape(colors, 0.27, 0.34);
  parts.torso.add(cape);
  parts.cape = cape;

  return { body, parts, height: 1.02 };
}

// Which palette hue each node of the sculpt wears. The importer strips the
// source's baked atlas — one texture cannot make two armies — so every
// surface is painted here instead. A node absent from this table keeps
// three.js's default white, which is how a missed part announces itself.
const KING_SURFACE = {
  // The robe and the sleeves are one garment under the ermine, so they share
  // a hue: white-on-cream read as a single undifferentiated mass, which is
  // the opposite of the sculpt's own white-cloak-over-coloured-robe.
  royal: ['armR', 'armL', 'torso', 'hips'],
  cloth: ['cloak', 'cloakL', 'cloakR', 'legR', 'legL'],
  accent: ['crown', 'footR', 'footL'],
  gold: ['chain'],
  // hairBeard is cut from the same surface as head — see import_king.py's
  // hair_or_skin() — so it needs its own entry or it would fall through to
  // head's skin material, same as before the cut.
  hair: ['hairBeard'],
  skin: ['head', 'handR', 'handL']
};

function buildKingSculpt(colors) {
  const built = rig('king');

  const surface = new Map();
  for (const [kind, names] of Object.entries(KING_SURFACE)) {
    const material =
      kind === 'royal' ? clothMat(colors.royal)
      : kind === 'cloth' ? clothMat(colors.cloth)
      : kind === 'accent' ? clothMat(colors.accent)
      : kind === 'hair' ? clothMat(colors.hair)
      : kind === 'gold' ? goldMat(colors.accent)
      : mat(colors.skin, { rough: 0.7 });
    for (const name of names) surface.set(name, material);
  }

  const parts = {};
  for (const [name, node] of built.nodes) {
    parts[name] = node;
    const material = surface.get(name);
    // Painted per node rather than by traverse-from-the-root, so a child that
    // wants its own hue (the cloak panels under the arms) is not overwritten
    // by its parent's pass.
    if (material) {
      node.traverse((o) => {
        if (o.isMesh && (o === node || !surface.has(o.name))) o.material = material;
      });
    }
  }

  // Hand-tuned in the editor and folded in as the default: the same accent
  // hue KING_SURFACE already painted it, but lacquered rather than cloth.
  parts.crown?.traverse((o) => {
    if (o.isMesh) o.material = mat(colors.accent, { rough: 0.50, metal: 0.60 });
  });

  // The sculpt's hands are empty — the sword is grafted on here, the same way
  // every other armed piece gets its weapon, so `INVENTORY.k`'s weapon slot
  // and `swing()` both find what they expect.
  const sword = makeSword(colors, 0.46);
  // Placement tuned by hand in the model editor and folded in as the
  // default: hangs at the king's side rather than held close to the fist.
  sword.position.set(0.003, -0.018, 0.018);
  sword.rotation.set(0.21, 1.48, 0.27);
  // A single lacquered-accent finish across blade, guard, grip and pommel,
  // replacing makeSword's usual steel-and-gold split — the same uniform
  // finish the editor applies across every mesh a part owns.
  sword.traverse((o) => { if (o.isMesh) o.material = mat(colors.accent, { rough: 0.50, metal: 0.60 }); });
  (parts.handR ?? built.root).add(sword);
  parts.weapon = sword;

  const group = new THREE.Group();
  group.add(built.root);
  return { body: group, parts, height: 0.95, rigged: true };
}

function buildKing(colors) {
  if (rigReady('king')) return buildKingSculpt(colors);
  const { body, parts } = buildHumanoid(colors, {
    scale: 1.02,
    torsoColor: colors.metal,
    armColor: colors.metal,
    legColor: colors.clothDark,
    shoulderWidth: 0.19,
    torsoHeight: 0.32
  });

  // Heavy layered pauldrons read as armour from across the board.
  for (const side of [-1, 1]) {
    const pauldron = sculpted('pauldron', metalMat(colors.metal), () => {
      const g = new THREE.SphereGeometry(0.075, 10, 10);
      g.scale(1, 0.7, 1);
      return g;
    });
    pauldron.position.set(side * 0.21, 0.3, 0);
    parts.torso.add(pauldron);
    parts[side < 0 ? 'pauldronL' : 'pauldronR'] = pauldron;
  }

  const tabard = box(0.2, 0.3, 0.02, mat(colors.accent, { rough: 0.8 }));
  tabard.position.set(0, 0.16, 0.075);
  parts.torso.add(tabard);
  parts.tabard = tabard;

  const beard = sculpted('beard', mat(0xe4e4dc, { rough: 0.95 }),
    () => new THREE.ConeGeometry(0.07, 0.17, 8));
  beard.position.set(0, -0.072, 0.030);
  beard.rotation.x = Math.PI;
  beard.scale.set(1.35, 0.85, 1.15);
  const crown = makeCrown(colors, 0.098, 5);
  crown.position.y = 0.08;
  parts.head.add(beard, crown);
  parts.beard = beard;
  parts.crown = crown;

  const sword = makeSword(colors, 0.46);
  sword.position.y = -0.1;
  parts.handR.add(sword);
  parts.weapon = sword;

  const cape = makeCape(colors, 0.32, 0.38);
  parts.torso.add(cape);
  parts.cape = cape;

  return { body, parts, height: 1.12 };
}

const BUILDERS = {
  p: buildPawn,
  n: buildKnight,
  b: buildBishop,
  r: buildRook,
  q: buildQueen,
  k: buildKing
};

export const PIECE_NAMES = {
  p: 'Pawn',
  n: 'Knight',
  b: 'Bishop',
  r: 'Rook',
  q: 'Queen',
  k: 'King'
};

/**
 * Builds a piece model.
 *
 * The returned group carries `userData.parts` (the armature joints) and
 * `userData.type` / `userData.color`, which the animator and picker rely on.
 * Black pieces face down-board so both armies look at each other.
 */
export function createPiece(type, color, variant = 0) {
  const colors = PALETTE[color];
  // Only the pawn reads `variant` — it picks which of three helmets this one
  // wears. Every other builder ignores the second argument.
  const built = BUILDERS[type](colors, variant);

  // The rest pose every `resetPose` call returns to — captured now, before
  // anything ever animates a joint, the same way the editor's `captureBase`
  // does. A procedural builder leaves every joint at rotation zero, so this
  // changes nothing for one; a rigged sculpt often doesn't — the queen's
  // arms sit bent at rest, baked into the Blender import — and `resetPose`
  // falls back to zero for any part with no entry here, which is exactly
  // what silently swapped her into the wrong "default" the moment she first
  // moved: nothing had ever told it bent arms *were* her default.
  const rest = {};
  for (const [name, node] of Object.entries(built.parts)) {
    if (Array.isArray(node) || !node?.isObject3D) continue;
    if (node.rotation.x || node.rotation.y || node.rotation.z) {
      rest[name] = [node.rotation.x, node.rotation.y, node.rotation.z];
    }
  }

  const root = new THREE.Group();

  // Models are authored at roughly two-thirds of a square. The scale lives on
  // an inner group rather than on `root`, because animations own `root.scale`
  // (the shatter and vanish deaths tween it, and resetPose returns it to 1).
  const scaler = new THREE.Group();
  scaler.scale.setScalar(1.34);
  root.add(scaler);

  scaler.add(buildBase(colors));

  const facing = new THREE.Group();
  facing.rotation.y = color === 'w' ? 0 : Math.PI;

  // The Blender parts are authored with their front toward -Y, which lands
  // backwards once glTF's Y-up export rotates the scene on the way out. This
  // corrects it once here rather than in every 0/Math.PI the animator swings
  // `facing` between, so lunges, recoils and turn-to-face-camera all still
  // work unchanged.
  const orient = new THREE.Group();
  orient.rotation.y = Math.PI;
  orient.add(built.body);
  built.body.position.y += 0.07; // Stand on top of the plinth.
  facing.add(orient);
  scaler.add(facing);

  root.traverse((o) => {
    if (o.isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });

  root.userData = {
    type,
    color,
    parts: built.parts,
    facing,
    body: built.body,
    height: built.height,
    // Sculpted pieces carry their pose in the geometry, so the animator must
    // not re-apply the primitive rig's hand-authored one on every reset.
    rigged: !!built.rigged,
    rest,
    name: PIECE_NAMES[type]
  };
  return root;
}
